/* SIL Export Pipeline — pixel-faithful port of SIL_Export_Pipeline_1.html.
 * Reference CSS is preserved verbatim inside a scoped .sil-wrap block so
 * it never bleeds into the rest of the app. Chart rendering follows the
 * reference SVG logic; live rows come from /api/sil-pipeline/invoices.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
const API = process.env.REACT_APP_BACKEND_URL || '';

const REFERENCE_CSS = `
.sil-wrap{--bg:#ffffff;--surface:#ffffff;--surface-2:#eaf0f2;--ink:#0e1a22;--ink-2:#44545e;--muted:#6b7b85;
  --line:#dbe3e7;--grid:#e8eef1;--accent:#0f6f8f;
  --s1:#2a78d6;--s2:#eb6834;--s3:#1baf7a;--s4:#eda100;
  --done:#256abf;--plan:#86b6ef;--good:#1a7f4b;--warn:#a86500;
  background:var(--bg);color:var(--ink);
  font-family:"Public Sans","Inter",system-ui,-apple-system,"Segoe UI",sans-serif;
  font-size:14px;line-height:1.45}
.sil-wrap *{box-sizing:border-box}
.sil-wrap .wrap{max-width:1180px;margin:0 auto;padding-inline:20px;padding-block:28px 48px;display:flex;flex-direction:column;gap:20px}
.sil-wrap .num,.sil-wrap .mono{font-family:"IBM Plex Mono",ui-monospace,Menlo,monospace;font-variant-numeric:tabular-nums}
.sil-wrap header{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:flex-end;gap:12px}
.sil-wrap .eyebrow{font-size:11px;letter-spacing:.09em;text-transform:uppercase;color:var(--muted);font-weight:600}
.sil-wrap h1{font-size:26px;line-height:1.15;margin:4px 0 0;font-weight:700;text-wrap:balance}
.sil-wrap .src{font-size:12px;color:var(--muted)}
.sil-wrap .controls{display:flex;flex-wrap:wrap;gap:10px 18px;align-items:center;background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:10px 14px}
.sil-wrap .ctl{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.sil-wrap .ctl>span{font-size:11px;letter-spacing:.07em;text-transform:uppercase;color:var(--muted);font-weight:600}
.sil-wrap .seg{display:inline-flex;border:1px solid var(--line);border-radius:7px;overflow:hidden}
.sil-wrap .seg button{border:0;background:transparent;color:var(--ink-2);font:inherit;font-size:13px;padding:5px 11px;cursor:pointer}
.sil-wrap .seg button+button{border-left:1px solid var(--line)}
.sil-wrap .seg button[aria-pressed="true"]{background:var(--ink);color:var(--surface);font-weight:600}
.sil-wrap .chip{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line);background:transparent;color:var(--ink-2);border-radius:99px;padding:4px 10px;font:inherit;font-size:12.5px;cursor:pointer}
.sil-wrap .chip i{width:10px;height:10px;border-radius:3px;display:inline-block}
.sil-wrap .chip[aria-pressed="false"]{opacity:.45;text-decoration:line-through}
.sil-wrap button:focus-visible,.sil-wrap input:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.sil-wrap .kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px}
.sil-wrap .kpi{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:14px 16px;display:flex;flex-direction:column;gap:6px}
.sil-wrap .kpi .l{font-size:11.5px;color:var(--muted);font-weight:600;letter-spacing:.04em;text-transform:uppercase}
.sil-wrap .kpi .v{font-size:24px;font-weight:500;letter-spacing:-.01em}
.sil-wrap .kpi .split{display:flex;height:6px;border-radius:3px;overflow:hidden;background:var(--grid);gap:2px}
.sil-wrap .kpi .split b{background:var(--done)} .sil-wrap .kpi .split s{background:var(--plan);text-decoration:none}
.sil-wrap .kpi .sub{font-size:12px;color:var(--ink-2);display:flex;flex-direction:column;gap:2px}
.sil-wrap .kpi .sub>span{display:flex;justify-content:space-between;gap:6px;white-space:nowrap}
.sil-wrap .grid2{display:grid;grid-template-columns:1.55fr 1fr;gap:16px}
.sil-wrap .grid3{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
.sil-wrap .panel{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:16px 16px 12px;min-width:0}
.sil-wrap .panel h2{font-size:15px;margin:0;font-weight:650}
.sil-wrap .panel .cap{font-size:12px;color:var(--muted);margin:2px 0 10px}
.sil-wrap .legend{display:flex;flex-wrap:wrap;gap:6px 14px;font-size:12px;color:var(--ink-2);margin-bottom:6px}
.sil-wrap .legend span{display:inline-flex;align-items:center;gap:6px}
.sil-wrap .legend i{width:11px;height:11px;border-radius:3px;display:inline-block}
.sil-wrap .legend i.h{background:repeating-linear-gradient(45deg,var(--muted) 0 2px,transparent 2px 5px);border:1px solid var(--muted)}
.sil-wrap svg text{fill:var(--muted);font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11px}
.sil-wrap svg .lbl{fill:var(--ink-2);font-family:"Public Sans",system-ui,sans-serif;font-size:12px}
.sil-wrap svg .val{fill:var(--ink);font-size:11px}
.sil-wrap .tip{position:fixed;pointer-events:none;background:var(--surface);color:var(--ink);border:1px solid var(--line);box-shadow:0 6px 20px rgba(0,0,0,.14);border-radius:8px;padding:8px 10px;font-size:12px;z-index:10;min-width:170px}
.sil-wrap .tip .t{font-weight:650;margin-bottom:4px}
.sil-wrap .tip .r{display:flex;justify-content:space-between;gap:14px}
.sil-wrap .tip .r span:last-child{font-family:"IBM Plex Mono",monospace}
.sil-wrap .tbl-wrap{overflow-x:auto;max-height:460px;overflow-y:auto;border-top:1px solid var(--line)}
.sil-wrap table{border-collapse:collapse;width:100%;font-size:12.5px}
.sil-wrap th,.sil-wrap td{padding:7px 10px;text-align:left;border-bottom:1px solid var(--grid);white-space:nowrap}
.sil-wrap th{position:sticky;top:0;background:var(--surface);font-size:11px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);font-weight:600;cursor:pointer}
.sil-wrap td.n,.sil-wrap th.n{text-align:right}
.sil-wrap .pill{display:inline-block;padding:1px 8px;border-radius:99px;font-size:11.5px;font-weight:600}
.sil-wrap .pill.d{background:color-mix(in srgb,var(--good) 14%,transparent);color:var(--good)}
.sil-wrap .pill.x{background:var(--surface-2);color:var(--muted)}
.sil-wrap .pill.p{background:color-mix(in srgb,var(--warn) 16%,transparent);color:var(--warn)}
.sil-wrap .rt{display:inline-flex;align-items:center;gap:6px}.sil-wrap .rt i{width:8px;height:8px;border-radius:2px}
.sil-wrap .tbar{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px}
.sil-wrap input[type=search]{font:inherit;font-size:13px;padding:6px 10px;border:1px solid var(--line);border-radius:7px;background:var(--bg);color:var(--ink);width:240px;max-width:100%}
.sil-wrap .notes{font-size:12.5px;color:var(--ink-2);background:var(--surface-2);border-radius:10px;padding:12px 16px}
.sil-wrap .notes b{color:var(--ink)}
.sil-wrap .notes ul{margin:6px 0 0;padding-left:18px;display:flex;flex-direction:column;gap:3px}
.sil-wrap .routes{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}
.sil-wrap .rc{border:1px solid var(--line);border-top:4px solid var(--c);border-radius:9px;padding:12px 14px;display:flex;flex-direction:column;gap:8px;min-width:0}
.sil-wrap .rc.off{opacity:.4}
.sil-wrap .rc h3{margin:0;font-size:13.5px;font-weight:650;line-height:1.3;min-height:2.6em}
.sil-wrap .rc .big{font-size:22px;font-weight:500}
.sil-wrap .rc .share{font-size:12px;color:var(--muted)}
.sil-wrap .rc dl{display:grid;grid-template-columns:auto 1fr;gap:3px 10px;margin:0;font-size:12px}
.sil-wrap .rc dt{color:var(--muted)} .sil-wrap .rc dd{margin:0;text-align:right;font-family:"IBM Plex Mono",monospace;font-variant-numeric:tabular-nums}
.sil-wrap .rc .tops{font-size:12px;color:var(--ink-2);border-top:1px solid var(--grid);padding-top:7px;display:flex;flex-direction:column;gap:3px}
.sil-wrap .rc .tops b{font-weight:600;color:var(--muted);font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;margin-top:3px}
.sil-wrap .rc .tr{display:flex;justify-content:space-between;gap:8px}.sil-wrap .rc .tr span:first-child{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.sil-wrap .rsplit{display:grid;grid-template-columns:1fr 1.2fr;gap:20px;margin-bottom:16px}
@media (max-width:980px){.sil-wrap .routes{grid-template-columns:repeat(2,minmax(0,1fr))}.sil-wrap .rsplit{grid-template-columns:1fr}.sil-wrap .kpis{grid-template-columns:repeat(3,minmax(0,1fr))}.sil-wrap .grid2,.sil-wrap .grid3{grid-template-columns:1fr}}
@media (max-width:560px){.sil-wrap .routes{grid-template-columns:1fr}.sil-wrap .kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.sil-wrap .kpi .v{font-size:20px}.sil-wrap h1{font-size:22px}}
@media (prefers-reduced-motion:no-preference){.sil-wrap svg rect.m{transition:opacity .15s}}
`;

// Load the Public Sans + IBM Plex Mono fonts once.
function useReferenceFonts() {
  useEffect(() => {
    const id = 'sil-pipeline-fonts';
    if (document.getElementById(id)) return;
    const l = document.createElement('link');
    l.id = id; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap';
    document.head.appendChild(l);
  }, []);
}

// ---------- Reference route registry (order 0..3 must match SVG code) ----------
const REF_ROUTES = [
  { n: 'Blue Earth prod. + SIL ship',       c: '--s4' },
  { n: 'SIL prod. + SIL ship',              c: '--s1' },
  { n: 'SIL prod. + WC ship (Russia)',      c: '--s2' },
  { n: 'WC prod. + WC ship (China, BT)',    c: '--s3' },
];
const ROUTE_TO_IDX = {
  'Blue Earth Production + SIL Shipment': 0,
  'SIL Production + SIL Shipment': 1,
  'SIL Production + WC Shipment (Russia)': 2,
  'WC Production + WC Shipment (China – BT)': 3,
  'WC Production + WC Shipment (China - BT)': 3,
};
const MON = ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov'];

// ---------- The reference dashboard render — direct port of the .html script ----------
function renderReferenceDashboard(root, rows, CANCELLED) {
  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => root.querySelectorAll(sel);
  const SHEET = [...rows, ...CANCELLED].sort((a, b) => a.sno - b.sno);
  const st = { status: 'all', metric: 'cont', routes: new Set([0,1,2,3]), q: '', sort: { k: 'sno', d: 1 }, match: false };
  const D = { R: REF_ROUTES };
  const css = (v) => getComputedStyle(root.querySelector('.sil-wrap') || root).getPropertyValue(v).trim();
  const fUSD = (v) => '$' + (v / 1e6).toFixed(2) + 'M';
  const fCr  = (v) => '₹' + (v / 1e7).toFixed(2) + ' Cr';
  const fmt  = (v, m) => m === 'cont' ? String(Math.round(v)) : m === 'usd' ? fUSD(v) : fCr(v);
  const fAxis = (v, m) => m === 'cont' ? String(v)
    : m === 'usd' ? '$' + (v / 1e6).toFixed(v % 1e6 ? 1 : 0) + 'M'
    : '₹' + (v / 1e7).toFixed(v % 1e7 ? 1 : 0) + 'Cr';
  const MLAB = { cont: 'Shipments', usd: 'Invoice value (USD)', inr: 'Invoice value (INR)', inc: 'Incentive 6.5% (INR)' };
  const inr = (v) => Math.round(v).toLocaleString('en-IN');
  const filtered = () => rows.filter((r) => st.routes.has(r.route) && (st.status === 'all' || (st.status === 'done') === r.done));
  const statusOnly = () => rows.filter((r) => st.status === 'all' || (st.status === 'done') === r.done);
  const nice = (max) => {
    if (max <= 0) return { top: 1, step: 1 };
    const raw = max / 4, p = Math.pow(10, Math.floor(Math.log10(raw))), n = raw / p;
    const s = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
    return { top: Math.ceil(max / s) * s, step: s };
  };
  const tip = $('.tip');
  const showTip = (e, html) => {
    tip.innerHTML = html; tip.hidden = false;
    const w = tip.offsetWidth, h = tip.offsetHeight;
    let x = e.clientX + 14, y = e.clientY + 14;
    if (x + w > window.innerWidth - 8) x = e.clientX - w - 14;
    if (y + h > window.innerHeight - 8) y = e.clientY - h - 14;
    tip.style.left = x + 'px'; tip.style.top = y + 'px';
  };
  const hideTip = () => { tip.hidden = true; };
  const NS = 'http://www.w3.org/2000/svg';
  const el = (t, a, p) => {
    const e = document.createElementNS(NS, t);
    for (const k in a) e.setAttribute(k, a[k]);
    if (p) p.appendChild(e);
    return e;
  };
  const hatchDefs = (svg) => {
    const d = el('defs', {}, svg);
    ['s1','s2','s3','s4','done','plan'].forEach((k) => {
      const p = el('pattern', { id: 'h-' + k, width: 6, height: 6, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)' }, d);
      el('rect', { width: 6, height: 6, fill: css('--' + k), opacity: 0.28 }, p);
      el('rect', { width: 2.4, height: 6, fill: css('--' + k) }, p);
    });
  };
  const rr = (x, y, w, h, r, top) => {
    r = Math.min(r, h, w / 2);
    if (!top || r <= 0) return `M${x},${y}h${w}v${h}h${-w}Z`;
    return `M${x},${y+h}V${y+r}Q${x},${y} ${x+r},${y}H${x+w-r}Q${x+w},${y} ${x+w},${y+r}V${y+h}Z`;
  };
  const hpath = (x, y, w, h) => {
    const r = Math.min(4, w / 2, h / 2);
    if (w <= 0) return '';
    return `M${x},${y}H${x+w-r}Q${x+w},${y} ${x+w},${y+r}V${y+h-r}Q${x+w},${y+h} ${x+w-r},${y+h}H${x}Z`;
  };

  function monthChart(data) {
    const host = $('#chMonth'); host.innerHTML = '';
    const m = st.metric, W = Math.max(320, host.clientWidth), H = 300, pl = 54, pr = 8, pt = 18, pb = 28;
    const cells = MON.map((_, i) => ({ i, segs: [] }));
    const agg = {}; data.forEach((r) => { const k = r.m + '|' + r.done + '|' + r.route; agg[k] = (agg[k] || 0) + r[m]; });
    const tot = MON.map(() => 0);
    MON.forEach((_, i) => {
      [true, false].forEach((dn) => [1,2,3,0].forEach((rt) => {
        const v = agg[i + '|' + dn + '|' + rt];
        if (v) { cells[i].segs.push({ done: dn, rt, v }); tot[i] += v; }
      }));
    });
    const { top, step } = nice(Math.max(...tot));
    const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, width: '100%', role: 'img', 'aria-label': 'Monthly shipments stacked by route' }, host);
    hatchDefs(svg);
    const y = (v) => pt + (H - pt - pb) * (1 - v / top), bw = (W - pl - pr) / MON.length, barW = Math.min(46, bw * 0.62);
    for (let v = 0; v <= top + 1e-9; v += step) {
      el('line', { x1: pl, x2: W - pr, y1: y(v), y2: y(v), stroke: css(v ? '--grid' : '--line'), 'stroke-width': 1 }, svg);
      const t = el('text', { x: pl - 8, y: y(v) + 4, 'text-anchor': 'end' }, svg);
      t.textContent = fAxis(v, m);
    }
    cells.forEach((c) => {
      const x = pl + bw * c.i + (bw - barW) / 2; let acc = 0;
      const t = el('text', { x: x + barW / 2, y: H - 8, 'text-anchor': 'middle', class: 'lbl' }, svg);
      t.textContent = MON[c.i];
      c.segs.forEach((s, j) => {
        const y0 = y(acc), y1 = y(acc + s.v); acc += s.v;
        const last = j === c.segs.length - 1;
        const key = D.R[s.rt].c.slice(2);
        const h = Math.max(0, y0 - y1 - (last ? 0 : 2));
        const p = el('path', { d: rr(x, y1, barW, h, 4, last), fill: s.done ? css('--' + key) : `url(#h-${key})`, class: 'm' }, svg);
        p.addEventListener('mousemove', (e) => showTip(e,
          `<div class="t">${MON[c.i]} 2026 · ${s.done ? 'Completed' : 'Planned'}</div>
           <div class="r"><span>${D.R[s.rt].n}</span><span>${fmt(s.v, m)}</span></div>
           <div class="r" style="color:var(--muted)"><span>Month total</span><span>${fmt(tot[c.i], m)}</span></div>`));
        p.addEventListener('mouseleave', hideTip);
      });
      if (tot[c.i]) {
        const v = el('text', { x: x + barW / 2, y: y(tot[c.i]) - 6, 'text-anchor': 'middle', class: 'val' }, svg);
        v.textContent = m === 'cont' ? tot[c.i] : fmt(tot[c.i], m).replace(' Cr', '');
      }
    });
    const lg = $('#legMonth'); lg.innerHTML = '';
    [1,2,3,0].filter((i) => st.routes.has(i)).forEach((i) => {
      lg.insertAdjacentHTML('beforeend', `<span><i style="background:var(${D.R[i].c})"></i>${D.R[i].n}</span>`);
    });
    lg.insertAdjacentHTML('beforeend', `<span><i class="h"></i>Planned</span>`);
  }

  function hbar(id, data, key, limit) {
    const host = $('#' + id); host.innerHTML = '';
    const m = st.metric;
    const g = {}; data.forEach((r) => {
      const k = r[key]; g[k] = g[k] || { d: 0, p: 0, n: 0 };
      g[k][r.done ? 'd' : 'p'] += r[m]; g[k].n++;
    });
    let arr = Object.entries(g).map(([k, v]) => ({ k, ...v, t: v.d + v.p })).sort((a, b) => b.t - a.t);
    if (limit && arr.length > limit) arr = arr.slice(0, limit);
    if (!arr.length) { host.innerHTML = '<p class="cap">No invoices match these filters.</p>'; return; }
    const W = Math.max(300, host.clientWidth), row = 28, lw = Math.min(170, W * 0.38), vr = 76, H = arr.length * row + 6;
    const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, width: '100%', role: 'img' }, host);
    const max = arr[0].t, sx = (v) => (W - lw - vr) * v / max;
    arr.forEach((a, i) => {
      const y = i * row + 4, bh = 16;
      const t = el('text', { x: lw - 10, y: y + bh / 2 + 4, 'text-anchor': 'end', class: 'lbl' }, svg);
      t.textContent = a.k.length > 24 ? a.k.slice(0, 23) + '…' : a.k;
      const wd = sx(a.d), wp = sx(a.p);
      const g2 = el('g', {}, svg);
      el('rect', { x: lw, y: y - 4, width: W - lw, height: row, fill: 'transparent' }, g2);
      if (wd > 0) el('path', { d: wp > 0 ? rr(lw, y, Math.max(0, wd - 1), bh, 0, false) : hpath(lw, y, wd, bh), fill: css('--done') }, g2);
      if (wp > 0) el('path', { d: hpath(lw + wd + (wd > 0 ? 1 : 0), y, Math.max(0, wp - (wd > 0 ? 1 : 0)), bh), fill: css('--plan') }, g2);
      const v = el('text', { x: lw + wd + wp + 6, y: y + bh / 2 + 4, class: 'val' }, svg);
      v.textContent = fmt(a.t, m);
      g2.addEventListener('mousemove', (e) => showTip(e,
        `<div class="t">${a.k}</div>
         <div class="r"><span>Completed</span><span>${fmt(a.d, m)}</span></div>
         <div class="r"><span>Planned</span><span>${fmt(a.p, m)}</span></div>
         <div class="r" style="color:var(--muted)"><span>PIs</span><span>${a.n}</span></div>`));
      g2.addEventListener('mouseleave', hideTip);
    });
  }

  function kpis(data) {
    const K = [['cont','Shipments'],['usd','Invoice value USD'],['inr','Invoice value INR'],['fob','FOB value INR'],['inc','Incentive 6.5%']];
    $('#kpis').innerHTML = K.map(([k, l]) => {
      const d = data.filter((r) => r.done).reduce((s, r) => s + r[k], 0);
      const p = data.filter((r) => !r.done).reduce((s, r) => s + r[k], 0);
      const t = d + p, pct = t ? d / t * 100 : 0;
      const f = (v) => k === 'cont' ? Math.round(v) : k === 'usd' ? fUSD(v) : fCr(v);
      return `<div class="kpi"><div class="l">${l}</div><div class="v num">${f(t)}</div>
        <div class="split" title="${pct.toFixed(0)}% completed">${d ? `<b style="flex:${d}"></b>` : ''}${p ? `<s style="flex:${p}"></s>` : ''}</div>
        <div class="sub"><span><span>Completed</span><span class="num">${f(d)}</span></span><span><span>Planned</span><span class="num">${f(p)}</span></span></div></div>`;
    }).join('');
  }

  const COLS = [['sno','S No',1],['pi','PI No'],['buyer','Buyer'],['country','Country'],['product','Product'],['route','Route'],['m','Month'],['done','Status'],['usd','USD',1],['inr','INR (₹)',1],['fob','FOB (₹)',1],['inc','Incentive (₹)',1]];

  function table(data) {
    const base = st.match ? data : SHEET;
    const q = st.q.toLowerCase();
    let d = base.filter((r) => r.cancel ? (!q || (r.pi + ' cancelled').toLowerCase().includes(q)) : true)
                 .filter((r) => r.cancel || !q || [r.pi, r.buyer, r.country, r.product].join(' ').toLowerCase().includes(q));
    const { k, d: dir } = st.sort;
    d = [...d].sort((a, b) => {
      if ((a.cancel || b.cancel) && k !== 'sno') { if (a.cancel && b.cancel) return a.sno - b.sno; return a.cancel ? 1 : -1; }
      let x = a[k], y = b[k];
      if (k === 'route') { x = D.R[x].n; y = D.R[y].n; }
      return (x > y ? 1 : x < y ? -1 : 0) * dir || (a.sno - b.sno);
    });
    $('#tbl thead').innerHTML = '<tr>' + COLS.map(([c, l, n]) =>
      `<th class="${n ? 'n' : ''}" data-k="${c}" aria-sort="${k === c ? (dir > 0 ? 'ascending' : 'descending') : 'none'}">${l}${k === c ? (dir > 0 ? ' ▲' : ' ▼') : ''}</th>`).join('') + '</tr>';
    $('#tbl tbody').innerHTML = d.map((r) => r.cancel
      ? `<tr style="color:var(--muted)"><td class="n num">${r.sno}</td><td class="mono"><s>${r.pi}</s></td><td colspan="5"></td><td><span class="pill x">✕ Cancelled</span></td><td colspan="4"></td></tr>`
      : `<tr><td class="n num">${r.sno}</td><td class="mono">${r.pi}</td><td>${r.buyer}</td><td>${r.country}</td><td>${r.product}</td>
         <td><span class="rt"><i style="background:var(${D.R[r.route].c})"></i>${D.R[r.route].n}</span></td>
         <td>${MON[r.m]}-26</td>
         <td><span class="pill ${r.done ? 'd' : 'p'}">${r.done ? '✓ Completed' : '◷ Planned'}</span></td>
         <td class="n num">$${Math.round(r.usd).toLocaleString('en-US')}</td>
         <td class="n num">${inr(r.inr)}</td><td class="n num">${inr(r.fob)}</td><td class="n num">${inr(r.inc)}</td></tr>`).join('');
    $('#capTbl').textContent = st.match
      ? `${d.length} of ${rows.length} active PIs match the dashboard filters`
      : (q ? `${d.length} of ${SHEET.length} PIs match "${st.q}"` : `All ${SHEET.length} PIs in SIL sheet order: ${rows.length} active, ${CANCELLED.length} cancelled`);
    $$('#tbl th').forEach((th) => th.onclick = () => {
      const c = th.dataset.k;
      st.sort = { k: c, d: st.sort.k === c ? -st.sort.d : (['usd','inr','fob','inc'].includes(c) ? -1 : 1) };
      table(filtered());
    });
  }

  function routeViews() {
    const m = st.metric, d = statusOnly();
    const SH = { 0: 'Blue Earth → SIL', 1: 'SIL → SIL', 2: 'SIL → WC (Russia)', 3: 'WC → WC (China)' };
    hbar('chRoute', d.map((r) => ({ ...r, rn: SH[r.route] })), 'rn');
    const host = $('#chRouteMonth'); host.innerHTML = '';
    const W = Math.max(300, host.clientWidth), rowH = 50, pl = Math.min(150, W * 0.34), pr = 8, H = 4 * rowH + 22;
    const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, width: '100%', role: 'img', 'aria-label': 'Monthly measure per route' }, host);
    hatchDefs(svg);
    const agg = {}; let max = 0;
    d.forEach((r) => { const k = r.route + '|' + r.m + '|' + r.done; agg[k] = (agg[k] || 0) + r[m]; });
    [0,1,2,3].forEach((rt) => MON.forEach((_, i) => {
      max = Math.max(max, (agg[rt + '|' + i + '|true'] || 0) + (agg[rt + '|' + i + '|false'] || 0));
    }));
    const bw = (W - pl - pr) / MON.length, barW = Math.min(26, bw * 0.6);
    MON.forEach((mn, i) => {
      const t = el('text', { x: pl + bw * i + bw / 2, y: H - 6, 'text-anchor': 'middle' }, svg);
      t.textContent = mn;
    });
    const SHORT = { 0: 'Blue Earth → SIL', 1: 'SIL → SIL', 2: 'SIL → WC (Russia)', 3: 'WC → WC (China)' };
    [1,2,3,0].forEach((rt, ri) => {
      const y0 = ri * rowH, base = y0 + rowH - 6, hh = rowH - 14, key = D.R[rt].c.slice(2);
      const off = !st.routes.has(rt);
      const lb = el('text', { x: 0, y: base - hh / 2 + 4, class: 'lbl', opacity: off ? 0.4 : 1 }, svg);
      lb.textContent = SHORT[rt];
      el('line', { x1: pl, x2: W - pr, y1: base, y2: base, stroke: css('--line') }, svg);
      MON.forEach((mn, i) => {
        const dn = agg[rt + '|' + i + '|true'] || 0, pn = agg[rt + '|' + i + '|false'] || 0;
        if (!dn && !pn) return;
        const x = pl + bw * i + (bw - barW) / 2, hd = hh * dn / max, hp = hh * pn / max;
        const g = el('g', { opacity: off ? 0.4 : 1 }, svg);
        el('rect', { x: x - 4, y: y0, width: barW + 8, height: rowH, fill: 'transparent' }, g);
        if (hd) el('path', { d: rr(x, base - hd, barW, hd, 3, !hp), fill: css('--' + key) }, g);
        if (hp) el('path', { d: rr(x, base - hd - hp - (hd ? 1.5 : 0), barW, hp, 3, true), fill: `url(#h-${key})` }, g);
        g.addEventListener('mousemove', (e) => showTip(e,
          `<div class="t">${D.R[rt].n} · ${mn}-26</div>
           <div class="r"><span>Completed</span><span>${fmt(dn, m)}</span></div>
           <div class="r"><span>Planned</span><span>${fmt(pn, m)}</span></div>`));
        g.addEventListener('mouseleave', hideTip);
      });
    });
    const all = d.reduce((a, r) => a + r[m], 0);
    $('#routeCards').innerHTML = [1,2,3,0].map((rt) => {
      const rs = d.filter((r) => r.route === rt);
      const sum = (k) => rs.reduce((a, r) => a + r[k], 0);
      const dn = rs.filter((r) => r.done).reduce((a, r) => a + r[m], 0);
      const pn = sum(m) - dn;
      const top = (k, n) => {
        const g = {}; rs.forEach((r) => g[r[k]] = (g[r[k]] || 0) + r[m]);
        return Object.entries(g).sort((a, b) => b[1] - a[1]).slice(0, n)
          .map(([a, v]) => `<span class="tr"><span>${a}</span><span class="num">${fmt(v, m)}</span></span>`).join('') || '–';
      };
      return `<div class="rc${st.routes.has(rt) ? '' : ' off'}" style="--c:var(${D.R[rt].c})">
        <h3>${D.R[rt].n}</h3>
        <div><div class="big num">${fmt(sum(m), m)}</div><div class="share">${all ? (sum(m) / all * 100).toFixed(1) : 0}% of ${MLAB[m].toLowerCase()}</div></div>
        <div class="kpi" style="border:0;padding:0;background:none"><div class="split">${dn ? `<b style="flex:${dn}"></b>` : ''}${pn ? `<s style="flex:${pn}"></s>` : ''}</div></div>
        <dl><dt>Completed</dt><dd>${fmt(dn, m)}</dd><dt>Planned</dt><dd>${fmt(pn, m)}</dd>
          <dt>Shipments</dt><dd>${rs.length}</dd>
          <dt>Invoice USD</dt><dd>${fUSD(sum('usd'))}</dd>
          <dt>Invoice INR</dt><dd>${fCr(sum('inr'))}</dd>
          <dt>FOB INR</dt><dd>${fCr(sum('fob'))}</dd>
          <dt>Incentive</dt><dd>${fCr(sum('inc'))}</dd>
          <dt>Avg USD / shipment</dt><dd>${rs.length ? '$' + Math.round(sum('usd') / rs.length).toLocaleString('en-US') : '–'}</dd></dl>
        <div class="tops"><b>Top markets</b>${top('country', 3)}<b>Top buyers</b>${top('buyer', 3)}</div></div>`;
    }).join('');
  }

  function render() {
    const d = filtered();
    routeViews(); kpis(d); monthChart(d);
    hbar('chCountry', d, 'country'); hbar('chBuyer', d, 'buyer', 10); hbar('chProduct', d, 'product');
    table(d);
  }

  function seg(id, key) {
    $$('#' + id + ' button').forEach((b) => b.onclick = () => {
      st[key] = b.dataset.v;
      $$('#' + id + ' button').forEach((x) => x.setAttribute('aria-pressed', x === b));
      render();
    });
  }
  seg('fStatus', 'status'); seg('fMetric', 'metric');

  const fr = $('#fRoute');
  fr.innerHTML = '';
  [1,2,3,0].forEach((i) => {
    const b = document.createElement('button');
    b.className = 'chip'; b.setAttribute('aria-pressed', 'true');
    b.innerHTML = `<i style="background:var(${D.R[i].c})"></i>${D.R[i].n}`;
    b.onclick = () => {
      if (st.routes.has(i)) { if (st.routes.size > 1) st.routes.delete(i); }
      else st.routes.add(i);
      b.setAttribute('aria-pressed', st.routes.has(i)); render();
    };
    fr.appendChild(b);
  });
  $('#match').addEventListener('change', (e) => { st.match = e.target.checked; table(filtered()); });
  $('#reset').onclick = () => {
    st.status = 'all'; st.routes = new Set([0,1,2,3]);
    $$('#fStatus button').forEach((x) => x.setAttribute('aria-pressed', x.dataset.v === 'all'));
    fr.querySelectorAll('.chip').forEach((c) => c.setAttribute('aria-pressed', 'true'));
    render();
  };
  $('#q').addEventListener('input', (e) => { st.q = e.target.value; table(filtered()); });
  let rt;
  const onResize = () => { clearTimeout(rt); rt = setTimeout(render, 120); };
  window.addEventListener('resize', onResize);
  render();
  return () => window.removeEventListener('resize', onResize);
}

// ---------- React shell ----------
const SilPipelinePage = () => {
  useReferenceFonts();
  const rootRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let cleanup;
    (async () => {
      try {
        const [invRes, luRes] = await Promise.all([
          axios.get(`${API}/api/sil-pipeline/invoices`, { withCredentials: true }),
          axios.get(`${API}/api/sil-pipeline/last-updated`, { withCredentials: true }),
        ]);
        setLastUpdated(luRes.data);
        const inv = invRes.data.invoices || [];
        const rows = inv.filter((r) => r.status !== 'CANCELLED').map((r) => ({
          sno: r.s_no, pi: r.pi_no, buyer: r.buyer || 'Unknown', country: r.country || 'Unknown',
          product: r.product || 'Mixed', cont: 1,
          usd: r.invoice_usd || 0, inr: r.inr_value || 0, fob: r.fob_inr || 0, inc: r.incentive_inr || 0,
          m: r.shipment_month ? (parseInt(r.shipment_month.slice(5), 10) - 4) : 0,
          done: r.status === 'COMPLETED',
          route: ROUTE_TO_IDX[r.route] ?? 1,
        }));
        const CANCELLED = inv.filter((r) => r.status === 'CANCELLED').map((r) => ({
          sno: r.s_no, pi: r.pi_no, cancel: true,
        }));
        setReady(true);
        // Defer render to after DOM commit
        setTimeout(() => {
          if (rootRef.current) cleanup = renderReferenceDashboard(rootRef.current, rows, CANCELLED);
        }, 30);
      } catch (e) {
        console.error('SIL pipeline load failed', e);
        setReady(true);
      }
    })();
    return () => { if (cleanup) cleanup(); };
  }, []);

  return (
    <div className="sil-wrap" ref={rootRef} data-testid="sil-pipeline-page">
      <style>{REFERENCE_CSS}</style>
      <div className="wrap">
        <header>
          <div>
            <div className="eyebrow">Sharat Industries · Proforma invoice status · FY 2026-27</div>
            <h1>SIL Export Pipeline, Apr – Nov 2026</h1>
          </div>
          <div className="src">
            Source: PI STATUS.xlsx → sheet "SIL"
            {lastUpdated?.at && (
              <><br/>Last updated {new Date(lastUpdated.at).toLocaleString('en-IN')} by {lastUpdated.by || 'system'}</>
            )}
          </div>
        </header>

        <div className="controls" role="group" aria-label="Filters">
          <div className="ctl"><span>Status</span>
            <div className="seg" id="fStatus">
              <button data-v="all" aria-pressed="true">All</button>
              <button data-v="done" aria-pressed="false">Completed</button>
              <button data-v="plan" aria-pressed="false">Planned</button>
            </div>
          </div>
          <div className="ctl"><span>Measure</span>
            <div className="seg" id="fMetric">
              <button data-v="cont" aria-pressed="true">Shipments</button>
              <button data-v="usd" aria-pressed="false">Invoice USD</button>
              <button data-v="inr" aria-pressed="false">Invoice ₹</button>
              <button data-v="inc" aria-pressed="false">Incentive ₹</button>
            </div>
          </div>
          <div className="ctl"><span>Route</span>
            <div id="fRoute" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} />
          </div>
          <button className="chip" id="reset" style={{ marginLeft: 'auto' }}>Show all</button>
        </div>

        <section className="kpis" id="kpis" aria-label="Totals"></section>

        <div className="grid2">
          <section className="panel">
            <h2>Shipments by month</h2>
            <p className="cap" id="capMonth">Stacked by production + shipment route. Hatched = planned, solid = completed.</p>
            <div className="legend" id="legMonth"></div>
            <div id="chMonth"></div>
          </section>
          <section className="panel">
            <h2>Destination markets</h2>
            <p className="cap">Completed vs planned, sorted by total.</p>
            <div className="legend">
              <span><i style={{ background: 'var(--done)' }}></i>Completed</span>
              <span><i style={{ background: 'var(--plan)' }}></i>Planned</span>
            </div>
            <div id="chCountry"></div>
          </section>
        </div>

        <section className="panel">
          <h2>Route-wise view</h2>
          <p className="cap">Each production → shipment route on its own. Follows the Status and Measure filters; routes switched off above are dimmed.</p>
          <div className="rsplit">
            <div>
              <div className="legend">
                <span><i style={{ background: 'var(--done)' }}></i>Completed</span>
                <span><i style={{ background: 'var(--plan)' }}></i>Planned</span>
              </div>
              <div id="chRoute"></div>
            </div>
            <div>
              <div className="legend">
                <span style={{ color: 'var(--muted)' }}>Month by month, same scale for every route. Solid = completed, hatched = planned.</span>
              </div>
              <div id="chRouteMonth"></div>
            </div>
          </div>
          <div className="routes" id="routeCards"></div>
        </section>

        <div className="grid3">
          <section className="panel">
            <h2>Top buyers</h2>
            <p className="cap">Ten largest by the selected measure.</p>
            <div className="legend">
              <span><i style={{ background: 'var(--done)' }}></i>Completed</span>
              <span><i style={{ background: 'var(--plan)' }}></i>Planned</span>
            </div>
            <div id="chBuyer"></div>
          </section>
          <section className="panel">
            <h2>Product mix</h2>
            <p className="cap">Glaze % dropped from product codes; multi-product PIs grouped as Mixed.</p>
            <div className="legend">
              <span><i style={{ background: 'var(--done)' }}></i>Completed</span>
              <span><i style={{ background: 'var(--plan)' }}></i>Planned</span>
            </div>
            <div id="chProduct"></div>
          </section>
        </div>

        <section className="panel">
          <div className="tbar">
            <div>
              <h2>Proforma invoices</h2>
              <p className="cap" id="capTbl" style={{ margin: '2px 0 0' }}></p>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12.5, color: 'var(--ink-2)', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" id="match" /> Apply dashboard filters
              </label>
              <input type="search" id="q" placeholder="Search PI, buyer, country, product" aria-label="Search invoices" />
            </div>
          </div>
          <div className="tbl-wrap">
            <table id="tbl"><thead></thead><tbody></tbody></table>
          </div>
        </section>

        <div className="notes">
          <b>Data notes</b>
          <ul>
            <li>Totals reconcile with the workbook's Grand Total: 177 shipments, $22.98M invoice value, ₹217.93 Cr INR value, ₹11.99 Cr incentive.</li>
            <li>Country spelling "UZKISTHAN" / "UZBEKISHTAN" merged as Uzbekistan.</li>
            <li>SIL/PI/126 and 127 are marked CANCEL in the sheet: they appear in the invoice table but not in any totals.</li>
          </ul>
        </div>
      </div>
      <div className="tip" id="tip" hidden></div>
      {!ready && <div style={{ padding: 40, color: '#6b7b85' }}>Loading pipeline…</div>}
    </div>
  );
};

export default SilPipelinePage;
